/**
 * Handlers para formulário multi-step
 * Gerencia navegação entre etapas e validações específicas
 */

import { renderStepLayout, renderStep1, renderStep2 } from '../renderers/ofertaFormularioMultiStepRenderer.js';
import { maskTelefone, maskCPF, maskCEP } from '../utils/formMasks.js';
import { validateCPF, isAgeValid, convertBirthdateToISO } from '../utils/formValidations.js';
import { sendFormData, buildUtmParams } from '../utils/formApi.js';
import { setSelectPlaceholder, loadStates, loadCitiesByState } from '../utils/ibgeLocation.js';
import { isMundoZeroKmHost } from '../utils/attributionUtils.js';
import { resolveOfferId } from '../helpers/ofertaFormularioHelpers.js';
import {
    createFieldErrorSetter,
    validateOptinCheckboxes,
    focusFirstInvalidField,
    isPhoneValid,
    EMAIL_REGEX,
    EMAIL_REQUIRED_MESSAGE,
    EMAIL_INVALID_MESSAGE,
    PHONE_REQUIRED_MESSAGE,
    PHONE_INVALID_MESSAGE,
} from '../utils/formValidationUi.js';
import { OPTIN_FIELD_ORDER } from '../renderers/sharedCheckboxes.js';

/**
 * Ordem visual dos campos do Step 1 (braço B), usada para focar o primeiro
 * campo inválido ao clicar em "Continuar" (AC-6/AC-7).
 * @type {readonly string[]}
 */
const STEP1_FIELD_ORDER = Object.freeze(['name', 'email', 'phone', ...OPTIN_FIELD_ORDER]);

/**
 * Ordem visual dos campos do Step 2. `modelos-interesse` é um multiselect: a div
 * de erro usa esse id, mas o controle focável é o gatilho `modelos-trigger`.
 * @type {readonly (string|{field: string, control: string})[]}
 */
const STEP2_FIELD_ORDER = Object.freeze([
    'cpf',
    'birthdate',
    'cep',
    'state',
    'city',
    { field: 'modelos-interesse', control: 'modelos-trigger' },
    'quando-comprar',
    'planting-area',
    'tractor-power',
]);

/**
 * Configuração do multi-step handler
 * @param {{noCep?: boolean}} [options] - noCep=true (form-no-cep + LP de Interesse):
 *   Step 2 é renderizado sem CEP e com Estado/Cidade sempre obrigatórios (ver renderStep2).
 */
export function setupMultiStepHandlers(shadowRoot, config, ofertaData, showMessageFunc, hideMessageFunc, options = {}) {
    const cssPrefix = config.cssPrefix;
    const { noCep = false } = options;
    // Multi-step = UX de 2 steps (LP Interesse). UTM content sempre widget-form-2-steps,
    // mesmo quando noCep=true (form-no-cep + Interesse reaproveita este handler).
    const formModel = 'form-2-steps';
    // Única origem de mensagem de erro de campo neste handler (⚠️ + ARIA).
    const setFieldError = createFieldErrorSetter(shadowRoot, cssPrefix);

    /**
     * Config de atribuição só para o multi-step (LP Interesse):
     * - zera utmContent do CMS para o fallback por formModel (widget-form-2-steps)
     * - trata utmSource placeholder do CMS (mundo_zero_km) como vazio, para a engine
     *   de form-utm (computeAttribution / orgânico+referrer) poder vencer
     * Não altera formApi/attributionUtils — compatível com fix/widget-modular-form-utm.
     */
    function buildMultiStepUtmParams() {
        const sourceNorm = String(config.utmSource || '')
            .trim()
            .toLowerCase()
            .replace(/[_-]+/g, ' ')
            .replace(/\s+/g, ' ');
        const isCmsSourcePlaceholder =
            !sourceNorm ||
            sourceNorm === 'mundo zero km' ||
            sourceNorm === 'mundozerokm';
        const mediumNorm = String(config.utmMedium || '').trim().toLowerCase();

        return buildUtmParams(
            {
                ...config,
                utmContent: undefined,
                utmSource: isCmsSourcePlaceholder ? undefined : config.utmSource,
                utmMedium:
                    isCmsSourcePlaceholder && mediumNorm === 'organic'
                        ? undefined
                        : config.utmMedium,
            },
            ofertaData,
            undefined,
            { formModel }
        );
    }

    // Mesma regra do form-no-cep (Ofertas): aceita só o primeiro nome (≥ 2 chars)
    function isFullNameValid(nameValue) {
        const normalized = (nameValue || '').trim().replace(/\s+/g, ' ');
        const parts = normalized.split(' ').filter(Boolean);

        if (parts.length < 1) {
            return false;
        }

        return parts[0].length >= 2;
    }

    // Estado do formulário multi-step
    let currentStep = 1;
    let step1Data = {};
    let step1Valid = false;
    let step2Valid = false;
    let formStartFired = false;
    let cepValid = null; // null = não validado, true = válido, false = inválido
    let cepValidating = false;
    let cepCidade = null;
    let cepEstado = null;

    // Elementos
    const container = shadowRoot.querySelector(`#${cssPrefix}-form-container`);
    container.innerHTML = renderStepLayout(config, ofertaData);

    // Identificador canônico da oferta (SPEC.md > SDD "padronizacao-identificador-oferta-ga"
    // §4.1: hash/campaign_identifier → documentId → config.id → "sem-id") — mesmo usado
    // no envio do lead (step1OfertaId/ofertaId mais abaixo) e reportado no form_start pra
    // permitir correlacionar qual oferta estava sendo preenchida quando o usuário iniciou
    // o formulário.
    const formStartOfertaId = resolveOfferId(ofertaData, config);

    // Dispara form_start ao completar (blur com conteúdo) o primeiro campo digitado,
    // em qualquer um dos dois steps; campos atômicos (select/checkbox/radio) disparam
    // no próprio "change". Delegado no shadowRoot para sobreviver à troca Step1↔Step2.
    function fireFormStart(fieldName) {
        if (formStartFired) return;
        // Só marca como disparado depois de confirmar que o gtag está
        // disponível — senão, um blur antes do GTM carregar perderia o
        // form_start pra sempre nesta sessão (a flag ficava true sem nunca
        // ter enviado o evento).
        if (typeof window.gtag !== 'function') return;
        formStartFired = true;
        const formStartData = {
            form_id: 'form_widget_oferta_multistep',
            form_field: fieldName,
            offer_id: formStartOfertaId,
            // UTM content sempre widget-form-2-steps, mesmo quando noCep=true
            // (form-no-cep + LP Interesse reaproveita este handler) — mesma
            // convenção de buildMultiStepUtmParams/formModel acima.
            widget_type: 'form-2-steps',
        };

        // widget_version só é reportado dentro do portal mundozerokm.com.br.
        // Este handler é compartilhado por form-2-steps e form-no-cep + LP
        // Interesse — ambos são a versão B do teste (A é só form-no-cep + Ofertas).
        if (isMundoZeroKmHost()) {
            formStartData.widget_version = 'B';
        }

        window.gtag('event', 'form_start', formStartData);
    }

    shadowRoot.addEventListener('focusout', (e) => {
        const el = e.target;
        if (formStartFired || !el) return;
        const tag = el.tagName;
        if (tag !== 'INPUT' && tag !== 'TEXTAREA') return;
        const type = (el.type || '').toLowerCase();
        if (type === 'checkbox' || type === 'radio') return;
        if (!el.value || !String(el.value).trim()) return;
        fireFormStart(el.name || el.id);
    });

    shadowRoot.addEventListener('change', (e) => {
        const el = e.target;
        if (formStartFired || !el) return;
        const tag = el.tagName;
        const type = (el.type || '').toLowerCase();
        if (tag === 'SELECT' || type === 'checkbox' || type === 'radio') {
            fireFormStart(el.name || el.id);
        }
    });

    function getStepContentContainer() {
        return shadowRoot.querySelector(`#${cssPrefix}-step-content`);
    }

    /**
     * Navega para Step 1
     */
    function showStep1() {
        currentStep = 1;
        const stepContent = getStepContentContainer();
        if (!stepContent) return;
        stepContent.innerHTML = renderStep1(config, ofertaData);

        // Aplica animação somente no bloco de formulário
        stepContent.classList.add(`${cssPrefix}-form-step-back`);
        setTimeout(() => stepContent.classList.remove(`${cssPrefix}-form-step-back`), 400);

        // Setup listeners Step 1
        const form = shadowRoot.querySelector(`#${cssPrefix}-form-step1`);
        const nameInput = shadowRoot.querySelector(`#${cssPrefix}-name`);
        const phoneInput = shadowRoot.querySelector(`#${cssPrefix}-phone`);
        const emailInput = shadowRoot.querySelector(`#${cssPrefix}-email`);
        let nameTouched = false;
        let emailTouched = false;
        let phoneTouched = false;
        /** `name`s dos checkboxes de opt-in já interagidos pelo usuário. */
        const optinTouched = new Set();

        /**
         * @param {{showErrors?: boolean}} [options]
         * @returns {boolean}
         */
        function validateNameField({ showErrors = false } = {}) {
            const name = nameInput?.value.trim() || '';
            const shouldShowError = showErrors || nameTouched;

            if (!isFullNameValid(name)) {
                if (shouldShowError) setFieldError('name', 'Preencha o nome.');
                return false;
            }

            setFieldError('name', '');
            return true;
        }

        /**
         * @param {{showErrors?: boolean}} [options]
         * @returns {boolean}
         */
        function validateEmailField({ showErrors = false } = {}) {
            const email = emailInput?.value.trim() || '';
            const shouldShowError = showErrors || emailTouched;

            if (email.length === 0) {
                if (shouldShowError) setFieldError('email', EMAIL_REQUIRED_MESSAGE);
                return false;
            }

            if (!EMAIL_REGEX.test(email)) {
                if (shouldShowError) setFieldError('email', EMAIL_INVALID_MESSAGE);
                return false;
            }

            setFieldError('email', '');
            return true;
        }

        /**
         * @param {{showErrors?: boolean}} [options]
         * @returns {boolean}
         */
        function validatePhoneField({ showErrors = false } = {}) {
            const phone = phoneInput?.value || '';
            const digitsOnly = phone.replace(/\D/g, '');
            const shouldShowError = showErrors || phoneTouched;

            if (digitsOnly.length === 0) {
                if (shouldShowError) setFieldError('phone', PHONE_REQUIRED_MESSAGE);
                return false;
            }

            if (!isPhoneValid(phone)) {
                if (shouldShowError) setFieldError('phone', PHONE_INVALID_MESSAGE);
                return false;
            }

            setFieldError('phone', '');
            return true;
        }

        /**
         * Valida o Step 1 completo, marcando **todos** os campos inválidos.
         * O botão "Continuar" não é mais desabilitado por validade (AC-6): o
         * bloqueio acontece no clique.
         * @param {{showErrors?: boolean}} [options] `showErrors` força a exibição
         *   de todas as mensagens (tentativa de avançar de step).
         * @returns {boolean}
         */
        function validateStep1({ showErrors = false } = {}) {
            const nameValid = validateNameField({ showErrors });
            const emailValid = validateEmailField({ showErrors });
            const phoneValid = validatePhoneField({ showErrors });
            // Cada opt-in desmarcado recebe a própria mensagem (AC-9).
            const optinsValid = validateOptinCheckboxes(shadowRoot, cssPrefix, {
                showErrors,
                touched: optinTouched,
            });

            const isValid = nameValid && emailValid && phoneValid && optinsValid;

            step1Valid = isValid;

            return isValid;
        }

        // Campos de texto: o `input` nunca marca "touched" nem valida — só
        // aplica máscara quando o campo tem uma (AC-13: erro não aparece
        // enquanto o campo está em foco sendo digitado). Exibição de erro só
        // no `blur` e na tentativa de avanço/submit.
        nameInput?.addEventListener('blur', () => {
            nameTouched = true;
            validateStep1();
        });

        // Máscara de telefone (sem validação: só formata a cada tecla)
        phoneInput?.addEventListener('input', (e) => {
            e.target.value = maskTelefone(e.target.value);
        });

        phoneInput?.addEventListener('blur', () => {
            phoneTouched = true;
            validateStep1();
        });

        emailInput?.addEventListener('blur', () => {
            emailTouched = true;
            validateStep1();
        });

        // Validação de checkboxes
        const checkboxes = shadowRoot.querySelectorAll(`#${cssPrefix}-checkboxes input[type="checkbox"]`);
        checkboxes.forEach(checkbox => {
            checkbox.addEventListener('change', () => {
                optinTouched.add(checkbox.name);
                validateStep1();
            });
        });

        // Validação inicial
        validateStep1();

        // Validação Step 1 (campos básicos)
        form?.addEventListener('submit', (e) => {
            e.preventDefault();

            // Marca todos os campos inválidos e permanece no Step 1, focando o
            // primeiro inválido (AC-6/AC-7). Nada de tracking de avanço de step
            // quando o step não avança.
            if (!validateStep1({ showErrors: true })) {
                focusFirstInvalidField(shadowRoot, cssPrefix, STEP1_FIELD_ORDER);
                return;
            }

            // Dispara evento form_step2 ao clicar em Continuar
            if (typeof window.gtag === 'function') {
                const eventData = {
                    event_category: 'Form',
                    event_label: 'Step 1 Completo - Avançando para Step 2',
                    offer_id: resolveOfferId(ofertaData, config)

                };

                // widget_version só é reportado dentro do portal mundozerokm.com.br.
                // Este handler é compartilhado por form-2-steps e form-no-cep + LP
                // Interesse — ambos são a versão B do teste (A é só form-no-cep + Ofertas).
                if (isMundoZeroKmHost()) {
                    eventData.widget_version = 'B';
                }

                window.gtag('event', 'form_step_2', eventData);
            }

            handleStep1Submit(e);
        });
    }

    /**
     * Navega para Step 2
     */
    function showStep2() {
        currentStep = 2;
        const stepContent = getStepContentContainer();
        if (!stepContent) return;
        stepContent.innerHTML = renderStep2(config, ofertaData, { noCep });

        // Aplica animação somente no bloco de formulário
        stepContent.classList.add(`${cssPrefix}-form-step-enter`);
        setTimeout(() => stepContent.classList.remove(`${cssPrefix}-form-step-enter`), 400);

        // Setup listeners Step 2
        const form = shadowRoot.querySelector(`#${cssPrefix}-form-step2`);
        const cpfInput = shadowRoot.querySelector(`#${cssPrefix}-cpf`);
        const cepInput = shadowRoot.querySelector(`#${cssPrefix}-cep`);
        const birthDateInput = shadowRoot.querySelector(`#${cssPrefix}-birthdate`);
        const stateInput = shadowRoot.querySelector(`#${cssPrefix}-state`);
        const cityInput = shadowRoot.querySelector(`#${cssPrefix}-city`);

        // Elementos das perguntas LP interesse
        const plantingAreaSelect = shadowRoot.querySelector(`#${cssPrefix}-planting-area`);
        const tractorPowerSelect = shadowRoot.querySelector(`#${cssPrefix}-tractor-power`);
        const modelosInteresseCheckboxes = shadowRoot.querySelectorAll(`input[name="modelosInteresse"]`);
        const quandoComprarSelect = shadowRoot.querySelector(`#${cssPrefix}-quando-comprar`);

        let cpfTouched = false;
        let cepTouched = false;
        let birthDateTouched = false;
        let stateTouched = false;
        let cityTouched = false;
        let modelosInteresseTouched = false;
        let quandoComprarTouched = false;
        let plantingAreaTouched = false;
        let tractorPowerTouched = false;

        async function validateCepWithApi(maskedValue) {
            const digits = maskedValue.replace(/\D/g, '');
            cepValidating = true;
            cepValid = null;
            cepCidade = null;
            cepEstado = null;
            try {
                const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
                const data = await res.json();
                if (data.erro) {
                    cepValid = false;
                    setFieldError('cep', 'CEP inválido, preencha corretamente');
                } else {
                    cepValid = true;
                    cepCidade = data.localidade || null;
                    cepEstado = data.uf || null;
                    setFieldError('cep', '');
                }
            } catch {
                cepValid = true;
                setFieldError('cep', '');
            } finally {
                cepValidating = false;
                validateStep2();
            }
        }

        /**
         * Valida o Step 2 completo, marcando **todos** os campos inválidos.
         * O botão de envio não é mais desabilitado por validade (AC-1).
         * @param {{showErrors?: boolean}} [options]
         * @returns {boolean}
         */
        function validateStep2({ showErrors = false } = {}) {
            let isValid = true;

            // CPF obrigatório quando o campo existe
            if (cpfInput) {
                const cpfValue = cpfInput.value || '';
                if (!cpfValue) {
                    isValid = false;
                    if (showErrors || cpfTouched) {
                        setFieldError('cpf', 'CPF é obrigatório.');
                    }
                } else if (cpfValue.length !== 14 || !validateCPF(cpfValue)) {
                    isValid = false;
                    if (showErrors || cpfTouched) {
                        setFieldError('cpf', 'CPF inválido.');
                    }
                } else {
                    setFieldError('cpf', '');
                }
            }

            // Data de nascimento obrigatória quando o campo existe
            if (birthDateInput) {
                const birthDateValue = birthDateInput.value || '';
                if (!birthDateValue) {
                    isValid = false;
                    if (showErrors || birthDateTouched) {
                        setFieldError('birthdate', 'Data de nascimento é obrigatória.');
                    }
                } else if (!isAgeValid(birthDateValue)) {
                    isValid = false;
                    if (showErrors || birthDateTouched) {
                        setFieldError('birthdate', 'É preciso ter mais de 18 anos para ter acesso a esta oferta.');
                    }
                } else {
                    setFieldError('birthdate', '');
                }
            }

            // CEP obrigatório quando o campo existe
            if (cepInput) {
                const cepValue = cepInput.value || '';
                if (!cepValue) {
                    isValid = false;
                    if (showErrors || cepTouched) {
                        setFieldError('cep', 'CEP é obrigatório.');
                    }
                } else if (cepValue.length !== 9) {
                    isValid = false;
                    if (showErrors || cepTouched) {
                        setFieldError('cep', 'CEP deve conter 8 dígitos.');
                    }
                } else if (cepValid === false) {
                    isValid = false;
                    // mensagem já exibida pelo validateCepWithApi
                } else if (cepValid === null) {
                    // Aguardando resposta da API: o submit não pode prosseguir, mas
                    // o motivo precisa ficar visível — sem mensagem, `focusFirstInvalidField`
                    // não encontra campo inválido e o clique morre em silêncio.
                    isValid = false;
                    if (showErrors || cepTouched) {
                        setFieldError('cep', 'Aguarde a validação do CEP...');
                    }
                } else {
                    setFieldError('cep', '');
                }
            }

            // Valida Estado/Cidade quando os campos existem na tela
            if (stateInput) {
                if (stateInput.value.trim() === '') {
                    isValid = false;
                    if (showErrors || stateTouched) {
                        setFieldError('state', 'Selecione o estado.');
                    }
                } else {
                    setFieldError('state', '');
                }
            }

            if (cityInput) {
                if (cityInput.value.trim() === '') {
                    isValid = false;
                    if (showErrors || cityTouched) {
                        setFieldError('city', 'Informe a cidade.');
                    }
                } else {
                    setFieldError('city', '');
                }
            }

            // Valida modelos de interesse (pelo menos um selecionado, se existir)
            if (modelosInteresseCheckboxes.length > 0) {
                const hasSelectedModel = Array.from(modelosInteresseCheckboxes).some(cb => cb.checked);
                if (!hasSelectedModel) {
                    isValid = false;
                    if (showErrors || modelosInteresseTouched) {
                        setFieldError('modelos-interesse', 'Selecione pelo menos um modelo de interesse.', {
                            control: 'modelos-trigger',
                        });
                    }
                } else {
                    setFieldError('modelos-interesse', '', { control: 'modelos-trigger' });
                }
            }

            // Valida quando comprar (campo obrigatório se existir)
            if (quandoComprarSelect && quandoComprarSelect.value === '') {
                isValid = false;
                if (showErrors || quandoComprarTouched) {
                    setFieldError('quando-comprar', 'Selecione quando tem interesse em comprar.');
                }
            } else if (quandoComprarSelect) {
                setFieldError('quando-comprar', '');
            }

            // Valida área de plantio (campo obrigatório se existir)
            if (plantingAreaSelect && plantingAreaSelect.value === '') {
                isValid = false;
                if (showErrors || plantingAreaTouched) {
                    setFieldError('planting-area', 'Selecione a área de plantio.');
                }
            } else if (plantingAreaSelect) {
                setFieldError('planting-area', '');
            }

            // Valida potência do trator (campo obrigatório se existir)
            if (tractorPowerSelect && tractorPowerSelect.value === '') {
                isValid = false;
                if (showErrors || tractorPowerTouched) {
                    setFieldError('tractor-power', 'Selecione a potência do trator.');
                }
            } else if (tractorPowerSelect) {
                setFieldError('tractor-power', '');
            }

            step2Valid = isValid;

            return isValid;
        }

        // Máscaras: o `input` de campo de texto nunca marca "touched" nem
        // dispara validação com exibição de erro (AC-13) — só formata (e, no
        // CEP, dispara a consulta à API que preenche cidade/estado).
        cpfInput?.addEventListener('input', (e) => {
            e.target.value = maskCPF(e.target.value);
        });

        cpfInput?.addEventListener('blur', () => {
            cpfTouched = true;
            validateStep2();
        });

        cepInput?.addEventListener('input', (e) => {
            e.target.value = maskCEP(e.target.value);
            const masked = e.target.value;
            cepValid = null;
            cepCidade = null;
            cepEstado = null;
            if (masked.length === 9) {
                validateCepWithApi(masked);
            }
        });

        cepInput?.addEventListener('blur', () => {
            cepTouched = true;
            if (cepInput.value.length === 9 && cepValid === null && !cepValidating) {
                validateCepWithApi(cepInput.value);
            }
            validateStep2();
        });

        stateInput?.addEventListener('change', async () => {
            stateTouched = true;
            cityTouched = true;
            await loadCitiesByState(cityInput, stateInput.value, { logPrefix: '[MultiStep]' });
            validateStep2();
        });

        cityInput?.addEventListener('change', () => {
            cityTouched = true;
            validateStep2();
        });

        // Validação de data de nascimento (18+)
        birthDateInput?.addEventListener('input', () => {
            birthDateTouched = true;
            validateStep2();
        });

        birthDateInput?.addEventListener('change', () => {
            birthDateTouched = true;
            validateStep2();
        });

        birthDateInput?.addEventListener('blur', () => {
            birthDateTouched = true;
            validateStep2();
        });

        // Validação de área de plantio
        plantingAreaSelect?.addEventListener('change', () => {
            plantingAreaTouched = true;
            validateStep2();
        });

        // Validação de potência do trator
        tractorPowerSelect?.addEventListener('change', () => {
            tractorPowerTouched = true;
            validateStep2();
        });

        // Multiselect de modelos de interesse (mantém checkboxes name=modelosInteresse)
        const modelosMultiselect = shadowRoot.querySelector(`#${cssPrefix}-modelos-multiselect`);
        const modelosTrigger = shadowRoot.querySelector(`#${cssPrefix}-modelos-trigger`);
        const modelosDropdown = shadowRoot.querySelector(`#${cssPrefix}-modelos-dropdown`);
        const modelosTriggerText = modelosTrigger?.querySelector(`.${cssPrefix}-multiselect-trigger-text`);

        function updateModelosTriggerLabel() {
            if (!modelosTriggerText || !modelosTrigger) return;

            const selected = Array.from(modelosInteresseCheckboxes)
                .filter((cb) => cb.checked)
                .map((cb) => {
                    const label = cb.closest('label')?.querySelector(`.${cssPrefix}-checkbox-text`);
                    return label?.textContent?.trim() || cb.value;
                });

            if (selected.length === 0) {
                modelosTriggerText.textContent = 'Selecione os modelos*';
                modelosTrigger.classList.add('is-placeholder');
                return;
            }

            modelosTrigger.classList.remove('is-placeholder');
            if (selected.length === 1) {
                modelosTriggerText.textContent = selected[0];
                return;
            }

            modelosTriggerText.textContent = `${selected.length} modelos selecionados`;
        }

        function setModelosDropdownOpen(isOpen) {
            if (!modelosTrigger || !modelosDropdown) return;
            modelosDropdown.hidden = !isOpen;
            modelosTrigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        }

        if (modelosMultiselect && modelosTrigger && modelosDropdown) {
            updateModelosTriggerLabel();

            modelosTrigger.addEventListener('click', (event) => {
                event.preventDefault();
                setModelosDropdownOpen(modelosDropdown.hidden);
            });

            shadowRoot.addEventListener('click', (event) => {
                if (!modelosDropdown.hidden && !modelosMultiselect.contains(event.target)) {
                    setModelosDropdownOpen(false);
                }
            });

            modelosDropdown.addEventListener('keydown', (event) => {
                if (event.key === 'Escape') {
                    setModelosDropdownOpen(false);
                    modelosTrigger.focus();
                }
            });
        }

        // Validação de modelos de interesse (checkboxes no multiselect)
        modelosInteresseCheckboxes.forEach(checkbox => {
            checkbox.addEventListener('change', () => {
                modelosInteresseTouched = true;
                updateModelosTriggerLabel();
                validateStep2();
            });
        });

        // Validação de quando comprar
        quandoComprarSelect?.addEventListener('change', () => {
            quandoComprarTouched = true;
            validateStep2();
        });

        // Inicializa selects dinâmicos de localização quando existem no Step 2
        loadStates(stateInput, { logPrefix: '[MultiStep]' });
        if (cityInput) {
            setSelectPlaceholder(cityInput, 'Cidade*');
            cityInput.disabled = true;
        }

        validateStep2();

        // Submit final
        form?.addEventListener('submit', (e) => {
            // Marca todos os campos inválidos e foca/rola até o primeiro (AC-2).
            if (!validateStep2({ showErrors: true })) {
                e.preventDefault();
                focusFirstInvalidField(shadowRoot, cssPrefix, STEP2_FIELD_ORDER);
                return;
            }
            handleStep2Submit(e);
        });
    }

    /**
     * Handler do submit do Step 1
     */
    async function handleStep1Submit(e) {
        e.preventDefault();
        hideMessageFunc();

        const form = e.target;
        const formData = new FormData(form);

        // A validação (marcando todos os campos inválidos + foco no primeiro)
        // já aconteceu no listener de submit do Step 1, via validateStep1({showErrors:true}).
        // Aqui só coletamos os dados e avançamos.

        // Salva dados do Step 1
        step1Data = {
            name: formData.get('name'),
            email: formData.get('email'),
            phone: formData.get('phone'),
        };

        // Salva somente os checkboxes realmente enviados no form
        if (formData.has('opt_in_widget')) {
            step1Data.opt_in_widget = formData.get('opt_in_widget') === 'on';
        }

        if (formData.has('opt_in_aceite')) {
            step1Data.opt_in_aceite = formData.get('opt_in_aceite') === 'on';
        }

        if (formData.has('opt_in_ciente')) {
            step1Data.opt_in_ciente = formData.get('opt_in_ciente') === 'on';
        }

        if (formData.has('opt_in_declaro')) {
            step1Data.opt_in_declaro = formData.get('opt_in_declaro') === 'on';
        }

        // Dispara envio parcial do Step 1 sem bloquear a navegação para o Step 2
        const step1OfertaId = ofertaData?.hash || config.id || 'sem-id';
        const step1UtmParams = buildMultiStepUtmParams();

        void sendFormData(
            step1Data,
            step1OfertaId,
            step1UtmParams,
            { stepOne: true, stepTwo: false }
        ).catch((error) => {
            console.error('Erro ao enviar Step 1:', error);
        });

        // Avança para Step 2
        showStep2();
    }

    /**
     * Handler do submit final (Step 2)
     */
    async function handleStep2Submit(e) {
        e.preventDefault();
        hideMessageFunc();

        const form = e.target;
        const formData = new FormData(form);

        // A validação (marcando todos os campos inválidos + foco no primeiro)
        // já aconteceu no listener de submit do Step 2, via
        // validateStep2({showErrors:true}). Aqui só coletamos os dados.
        // As validações duplicadas que existiam aqui faziam early return no
        // primeiro erro, marcando um único campo — contrato substituído pela
        // AC-2 ("todos os campos inválidos marcados").
        const cpfValue = formData.get('cpf');
        const birthDateValue = formData.get('birthDate');
        const cepValue = formData.get('zipCode');
        const stateValue = formData.get('state');
        const cityValue = formData.get('city');
        const modelosInteresseValues = formData.getAll('modelosInteresse');
        const quandoComprarValue = formData.get('quandoComprar');

        // Combina dados dos 2 steps
        const completeData = {
            ...step1Data,
            cpf: cpfValue || undefined,
            zipCode: cepValue || undefined,
            state: stateValue || cepEstado || undefined,
            city: (cityValue ? cityValue.toString().trim() : null) || cepCidade || undefined,
            birthDate: birthDateValue ? convertBirthdateToISO(birthDateValue) : undefined,
            modelosInteresse: modelosInteresseValues.length > 0 ? modelosInteresseValues : undefined,
            quandoComprar: quandoComprarValue || undefined,
            plantingArea: formData.get('plantingArea') || undefined,
            tractorPower: formData.get('tractorPower') || undefined,
        };

        // Extrai UTMs e captura campaignIdentifier
        const utmParams = buildMultiStepUtmParams();

        // ID da oferta para envio (prioriza hash da oferta)
        const ofertaId = ofertaData?.hash || config.id || 'sem-id';

        // Envia para API
        try {
            showMessageFunc('Enviando dados...', 'info');

            // Dispara evento generated_lead via código (conforme documentação)
            if (typeof window.gtag === 'function') {
                const generateLeadData = {
                    event_category: 'Lead',
                    event_label: 'Form Step 2 Envio',
                    offer_id: resolveOfferId(ofertaData, config)
                };

                // widget_version só é reportado dentro do portal mundozerokm.com.br.
                // Este handler é compartilhado por form-2-steps e form-no-cep + LP
                // Interesse — ambos são a versão B do teste (A é só form-no-cep + Ofertas).
                if (isMundoZeroKmHost()) {
                    generateLeadData.widget_version = 'B';
                }

                window.gtag('event', 'generate_lead', generateLeadData);
            }

            const response = await sendFormData(
                completeData,
                ofertaId,
                utmParams,
                { stepOne: true, stepTwo: true }
            );

            if (response.ok || response.success) {
                // Dispara evento lead quando recebe 200 do MotordeLeads
                if (typeof window.gtag === 'function') {
                    const leadData = {
                        event_category: 'Lead',
                        event_label: 'Motor v2 Success',
                        widget: config.titleForm,
                        offer_id: resolveOfferId(ofertaData, config)
                    };

                    // widget_version só é reportado dentro do portal mundozerokm.com.br.
                    // Este handler é compartilhado por form-2-steps e form-no-cep + LP
                    // Interesse — ambos são a versão B do teste (A é só form-no-cep + Ofertas).
                    if (isMundoZeroKmHost()) {
                        leadData.widget_version = 'B';
                    }

                    window.gtag('event', 'lead', leadData);
                }

                // Pixel de conversão OpenAI Ads — só no domínio próprio mundozerokm.com.br
                // (SDK já é inicializado pelo portal via RootLayout; sites de parceiros
                // nunca carregam o SDK, então window.oaiq nem existiria lá).
                if (typeof window.oaiq === 'function' && isMundoZeroKmHost()) {
                    window.oaiq('measure', 'lead_created', { type: 'customer_action' });
                }

                showMessageFunc(config.successMessage || 'Dados enviados com sucesso!', 'success');

                // Reset
                showStep1();
            } else {
                throw new Error('Erro no envio');
            }
        } catch (error) {
            console.error('Erro ao enviar formulário:', error);
            showMessageFunc(config.errorMessage || 'Erro ao enviar dados. Tente novamente.', 'error');
        }
    }

    // Inicia no Step 1
    showStep1();
}
